/**
 * Helpers to display localized provider data (type, availability, status).
 * Uses translation keys when the API sends a known code; otherwise falls back to API display value.
 */

import { PROVIDER_TYPES, AVAILABILITY_STATUSES } from '@/types/provider'

type TFunction = (key: string) => string

const PROVIDER_TYPE_SET = new Set<string>(PROVIDER_TYPES)
const AVAILABILITY_SET = new Set<string>(AVAILABILITY_STATUSES)

/** Known status keys we have translations for */
const STATUS_KEYS: Record<string, string> = {
  active: 'status.active',
  inactive: 'status.inactive',
  suspended: 'status.suspended',
}

function normalizeStatusKey(value: string | null | undefined): string | null {
  if (value == null || value === '') return null
  const key = value.trim().toLowerCase().replace(/\s+/g, '')
  return key || null
}

/** Get localized provider type label (e.g. Physician → طبيب when locale is ar) */
export function getProviderTypeDisplay(
  providerType: string | null | undefined,
  providerTypeDisplay: string | null | undefined,
  t: TFunction
): string {
  const code = (providerType ?? '').trim().toLowerCase()
  if (code && PROVIDER_TYPE_SET.has(code)) {
    const translated = t(`providers.types.${code}`)
    if (translated && translated !== `providers.types.${code}`) return translated
  }
  return (providerTypeDisplay || providerType || '').trim() || '—'
}

/** Get localized availability status label */
export function getAvailabilityStatusDisplay(
  availabilityStatus: string | null | undefined,
  availabilityStatusDisplay: string | null | undefined,
  t: TFunction
): string {
  const code = (availabilityStatus ?? '').trim().toLowerCase()
  if (code && AVAILABILITY_SET.has(code as any)) {
    const translated = t(`providers.availabilityStatuses.${code}`)
    if (translated && translated !== `providers.availabilityStatuses.${code}`) return translated
  }
  return (availabilityStatusDisplay || availabilityStatus || '').trim() || '—'
}

/** Get localized provider status label (Active, Inactive, Suspended, etc.) */
export function getProviderStatusDisplay(
  status: string | null | undefined,
  statusDisplay: string | null | undefined,
  t: TFunction
): string {
  const key = normalizeStatusKey(status ?? statusDisplay)
  if (key && STATUS_KEYS[key]) {
    const translated = t(STATUS_KEYS[key])
    if (translated && translated !== STATUS_KEYS[key]) return translated
  }
  // Try status.${key} for other known keys (e.g. status.pending)
  if (key) {
    const translated = t(`status.${key}`)
    if (translated && translated !== `status.${key}`) return translated
  }
  return (statusDisplay || status || '').trim() || '—'
}
